/**
 * Iter322 — Extracted modals from FestivalPlayers.jsx to bring the
 * page under 1,200 lines. These three modals are self-contained UI
 * that reads props + calls a callback; no reach-back into page state.
 *
 *  · ClearAllModal      — Iter318a case-insensitive Delete-All confirm
 *  · NoShowTodayModal   — Iter318 per-day no-show flag
 *  · RemoveFromFestivalModal — Iter318 permanent removal (type REMOVE)
 */
import React from "react";
import { AlertOctagon, AlertTriangle } from "lucide-react";

export function ClearAllModal({
  open, onClose, onConfirm, busy, festivalName,
  confirmText, setConfirmText,
}) {
  if (!open) return null;
  const canConfirm = !busy && (confirmText || "").trim().toLowerCase() === (festivalName || "").trim().toLowerCase();
  return (
    <div
      className="fixed inset-0 z-[85] flex items-center justify-center bg-black/80 p-4"
      onClick={() => !busy && onClose()}
      data-testid="players-clear-modal"
    >
      <div className="w-full max-w-md bg-[#0A0A0A] border-2 border-[#FF4500] p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3 mb-3">
          <AlertOctagon className="w-6 h-6 text-[#FF4500] shrink-0 mt-0.5" />
          <div>
            <div className="label-mono text-[#FF4500] text-[10px]">CLEAR ALL PLAYERS · PERMANENT</div>
            <h2 className="h-display text-xl mt-1">Delete every player from this festival</h2>
          </div>
        </div>
        <p className="text-sm text-white mb-3">
          This wipes the entire roster for <span className="font-bold">{festivalName}</span>. It cannot be undone.
        </p>
        <label className="block text-[10px] uppercase tracking-widest text-[#A3A3A3] mb-1">
          Type the festival name to confirm (case-insensitive)
        </label>
        <input
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          autoFocus
          data-testid="players-clear-confirm-input"
          className="w-full bg-[#141414] border border-[#262626] focus:border-[#FF4500] focus:outline-none px-3 py-2 text-sm text-white mb-4"
        />
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            data-testid="players-clear-cancel"
            className="px-4 py-2 text-xs uppercase tracking-widest text-[#A3A3A3] hover:text-white disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!canConfirm}
            data-testid="players-clear-confirm"
            className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-widest font-bold bg-[#FF4500] text-white hover:bg-[#FF6633] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy ? "Deleting…" : "Delete all players"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function NoShowTodayModal({ open, target, busy, onCancel, onConfirm }) {
  if (!open || !target) return null;
  return (
    <div
      className="fixed inset-0 z-[86] flex items-center justify-center bg-black/80 p-4"
      onClick={() => !busy && onCancel()}
      data-testid="player-noshow-modal"
    >
      <div className="w-full max-w-md bg-[#0A0A0A] border border-[#FFB020] p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3 mb-3">
          <AlertTriangle className="w-6 h-6 text-[#FFB020] shrink-0 mt-0.5" />
          <div>
            <div className="label-mono text-[#FFB020] text-[10px]">NO SHOW TODAY</div>
            <h2 className="h-display text-xl mt-1">Mark for today only?</h2>
          </div>
        </div>
        <p className="text-sm text-white mb-3">
          <span className="font-bold">{target.first_name} {target.last_name}</span> will
          be greyed out for <span className="text-[#FFB020]">today only</span>. Their court seat is freed
          for check-in today. This action <span className="text-[#FFB020]">resets at midnight</span> — if they
          arrive tomorrow, they can be checked in without any action from you.
        </p>
        <p className="text-xs text-[#A3A3A3] mb-4">
          This does not free a permanent roster slot — the tier cap is untouched.
        </p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            data-testid="player-noshow-cancel"
            className="px-4 py-2 text-xs uppercase tracking-widest text-[#A3A3A3] hover:text-white disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            data-testid="player-noshow-confirm"
            className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-widest font-bold border border-[#FFB020] text-[#FFB020] hover:bg-[#FFB020] hover:text-black disabled:opacity-40"
          >
            {busy ? "Marking…" : "Mark no-show today"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function RemoveFromFestivalModal({
  open, target, busy, confirmText, setConfirmText, onCancel, onConfirm,
}) {
  if (!open || !target) return null;
  const canConfirm = !busy && (confirmText || "").trim().toLowerCase() === "remove";
  return (
    <div
      className="fixed inset-0 z-[86] flex items-center justify-center bg-black/80 p-4"
      onClick={() => !busy && onCancel()}
      data-testid="player-remove-modal"
    >
      <div className="w-full max-w-md bg-[#0A0A0A] border-2 border-[#FF4500] p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3 mb-3">
          <AlertOctagon className="w-6 h-6 text-[#FF4500] shrink-0 mt-0.5" />
          <div>
            <div className="label-mono text-[#FF4500] text-[10px]">REMOVE FROM FESTIVAL · PERMANENT</div>
            <h2 className="h-display text-xl mt-1">This cannot be undone</h2>
          </div>
        </div>
        <p className="text-sm text-white mb-3">
          <span className="font-bold">{target.first_name} {target.last_name}</span> will be
          permanently removed from this festival for <span className="text-[#FF4500]">all days</span> and
          cannot be reactivated. The cap high-water mark stays locked — deletion never re-opens Add Player.
        </p>
        <label className="block text-[10px] uppercase tracking-widest text-[#A3A3A3] mb-1">
          Type REMOVE to confirm
        </label>
        <input
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          autoFocus
          data-testid="player-remove-confirm-input"
          className="w-full bg-[#141414] border border-[#262626] focus:border-[#FF4500] focus:outline-none px-3 py-2 text-sm text-white mb-4"
        />
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            data-testid="player-remove-cancel"
            className="px-4 py-2 text-xs uppercase tracking-widest text-[#A3A3A3] hover:text-white disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!canConfirm}
            data-testid="player-remove-confirm"
            className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-widest font-bold bg-[#FF4500] text-white hover:bg-[#FF6633] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy ? "Removing…" : "Remove permanently"}
          </button>
        </div>
      </div>
    </div>
  );
}
